/**
 * Search keywords for command-palette navigation items.
 *
 * Nav labels are short ("Voice Log"), so people who type what they want to DO
 * ("dictate", "speak", "mic", "sprache", "voz", "voix", "صوت") would otherwise
 * find nothing. Keywords are kept per route and per language; the palette
 * searches every language's list (a bilingual user may type either) plus the
 * translated label.
 */

export const COMMAND_KEYWORD_LANGUAGES = ["en", "ar", "de", "es", "fr"] as const;
export type CommandKeywordLanguage = (typeof COMMAND_KEYWORD_LANGUAGES)[number];

export type CommandKeywordMap = Record<
  string,
  Record<CommandKeywordLanguage, readonly string[]>
>;

/** Keyed by canonical route name (the `-i18n` suffix is stripped by the caller). */
export const COMMAND_KEYWORDS: CommandKeywordMap = {
  "health-assistant-voice-log": {
    en: [
      "voice",
      "dictate",
      "speak",
      "microphone",
      "mic",
      "log",
      "transcribe",
      "audio",
      "record",
      "speech",
    ],
    ar: [
      "صوت",
      "صوتي",
      "تسجيل",
      "إملاء",
      "تحدث",
      "ميكروفون",
      "ميكرفون",
      "تفريغ",
      "نسخ",
      "كلام",
    ],
    de: [
      "sprache",
      "sprachaufnahme",
      "stimme",
      "diktieren",
      "sprechen",
      "mikrofon",
      "aufnahme",
      "transkribieren",
      "audio",
      "protokoll",
    ],
    es: [
      "voz",
      "dictar",
      "hablar",
      "micrófono",
      "grabar",
      "grabación",
      "transcribir",
      "audio",
      "registro",
      "habla",
    ],
    fr: [
      "voix",
      "vocal",
      "dicter",
      "parler",
      "microphone",
      "micro",
      "enregistrer",
      "transcrire",
      "audio",
      "journal",
    ],
  },
  "health-assistant": {
    en: ["assistant", "ai", "chat", "coach", "ask", "question", "help", "health"],
    ar: ["مساعد", "ذكاء", "ذكاء اصطناعي", "دردشة", "محادثة", "سؤال", "مساعدة", "صحة"],
    de: ["assistent", "ki", "chat", "frage", "fragen", "hilfe", "gesundheit"],
    es: ["asistente", "ia", "chat", "pregunta", "preguntar", "ayuda", "salud"],
    fr: ["assistant", "ia", "chat", "question", "aide", "santé", "demander"],
  },
};

export function canonicalRouteName(routeName: string): string {
  return routeName.replace(/-i18n$/, "");
}

/** All keywords for a route, across every language, in a stable order. */
export function getCommandKeywords(routeName: string): string[] {
  const entry = COMMAND_KEYWORDS[canonicalRouteName(routeName)];
  if (!entry) return [];
  const seen = new Set<string>();
  for (const lang of COMMAND_KEYWORD_LANGUAGES) {
    for (const word of entry[lang]) seen.add(word);
  }
  return Array.from(seen);
}

/**
 * Lowercase, strip diacritics (é -> e, Arabic tashkeel), and fold Arabic
 * alef/ya/ta-marbuta variants so "إملاء" and "املاء" compare equal.
 */
export function normalizeSearchText(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ًͯ-ٰٟـ]/g, "")
    .replace(/[أإآٱ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .normalize("NFC")
    .trim();
}

function words(value: string): string[] {
  return normalizeSearchText(value).split(/[^\p{L}\p{N}]+/u).filter(Boolean);
}

/**
 * Every whitespace-separated query token must match. A token matches when it is
 * a substring of one of the `labels` (label / group text), or a prefix of one
 * of the words in `keywords` (prefix, not substring, so short keywords like
 * "ai" or "ki" don't match half the menu).
 */
export function matchesCommandQuery(
  query: string,
  labels: readonly string[],
  keywords: readonly string[],
): boolean {
  const tokens = normalizeSearchText(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const normalizedLabels = labels.map(normalizeSearchText);
  const keywordWords = keywords.flatMap(words);
  return tokens.every(
    (token) =>
      normalizedLabels.some((label) => label.includes(token)) ||
      keywordWords.some((word) => word.startsWith(token)),
  );
}
